import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import toast from 'react-hot-toast'
import {
  CogIcon,
  BellIcon,
  KeyIcon,
  ChartBarIcon,
  UserIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
  PlayIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline'

import { apiClient } from '../../lib/api'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import ConnectedAccounts from '../../components/settings/ConnectedAccounts'
import RedditAppSetupGuide from '../../components/settings/RedditAppSetupGuide'
import RedditAppCredentialsForm, { type RedditAppFormData } from '../../components/settings/RedditAppCredentialsForm'
import RedditOAuthButton from '../../components/settings/RedditOAuthButton'
import RedditAppUsageStats from '../../components/settings/RedditAppUsageStats'

// Real backend settings interface based on actual API response
interface BackendSettings {
  reddit_client_id: string
  reddit_client_secret: string
  reddit_client_secret_masked?: string
  reddit_user_agent: string
  openai_api_key: string
  openai_api_key_masked?: string
  openai_model: string
  anthropic_api_key: string
  anthropic_api_key_masked?: string
  ai_settings: {
    default_provider: 'openai' | 'anthropic'
    default_openai_model: string
    default_anthropic_model: string
    temperature: number
    max_tokens: number
    openai_models?: string[]
    anthropic_models?: string[]
  }
  monitoring_frequency: number
  daily_api_limit: number
  enable_auto_reply: boolean
  profile: {
    first_name: string
    last_name: string
    email: string
    display_name: string
    avatar_url: string
  }
  notifications: {
    email_opportunities: boolean
    email_responses: boolean
    browser_notifications: boolean
    slack_webhook: string
  }
  reddit_apps?: Array<{
    app_name: string
    current_usage: number
    daily_limit: number
    oauth_authorized: boolean
    health_score: number
    can_make_requests: boolean
  }>
  // New user_reddit_app field for user-specific Reddit app
  user_reddit_app?: {
    has_app: boolean
    app?: {
      id: number
      app_name: string
      client_id_masked?: string
      client_secret_masked?: string
      user_agent: string
      daily_limit: number
      current_usage: number
      status: string
      health_score: number
      oauth_authorized: boolean
      access_token_exists: boolean
      last_reset?: string
      created_at: string
      updated_at: string
    }
    can_make_requests: boolean
    usage_percentage: number
  }
  available_tabs?: string[]
  user_role?: string
  is_admin?: boolean
}

// Form validation schemas
const redditSchema = yup.object({
  reddit_client_id: yup.string().required('Client ID is required'),
  reddit_client_secret: yup.string().when('is_update', {
    is: false,
    then: (schema) => schema.required('Client Secret is required'),
    otherwise: (schema) => schema.optional()
  }),
  reddit_user_agent: yup.string().required('User Agent is required'),
})

const aiSchema = yup.object({
  openai_api_key: yup.string().optional(),
  anthropic_api_key: yup.string().optional(),
  ai_settings: yup.object({
    default_provider: yup.string().oneOf(['openai', 'anthropic']).required(),
    default_openai_model: yup.string().required(),
    default_anthropic_model: yup.string().required(),
    temperature: yup.number().min(0).max(2).required(),
    max_tokens: yup.number().min(50).max(4000).required(),
  })
})

const monitoringSchema = yup.object({
  monitoring_frequency: yup.number().min(1).max(60).required(),
  daily_api_limit: yup.number().min(10).max(10000).required(),
  enable_auto_reply: yup.boolean().required(),
})

const profileSchema = yup.object({
  profile: yup.object({
    first_name: yup.string().required('First name is required'),
    last_name: yup.string().required('Last name is required'),
    display_name: yup.string().required('Display name is required'),
    email: yup.string().email('Invalid email format').required('Email is required'),
  })
})

const notificationsSchema = yup.object({
  notifications: yup.object({
    email_opportunities: yup.boolean().required(),
    email_responses: yup.boolean().required(),
    browser_notifications: yup.boolean().required(),
    slack_webhook: yup.string().url('Invalid webhook URL').optional(),
  })
})

const passwordSchema = yup.object({
  current_password: yup.string().required('Current password is required'),
  new_password: yup.string()
    .required('New password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Must contain at least one lowercase letter')
    .matches(/[0-9]/, 'Must contain at least one number'),
  confirm_password: yup.string()
    .required('Please confirm your new password')
    .oneOf([yup.ref('new_password')], 'Passwords must match')
})

const SettingsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState(() => {
    // Initialize from URL query parameter
    return searchParams.get('tab') || 'overview'
  })
  const [showApiKeys, setShowApiKeys] = useState(false)
  const [showPasswords, setShowPasswords] = useState(false)
  const [testingConnection, setTestingConnection] = useState<string | null>(null)
  const queryClient = useQueryClient()

  // Sync URL with active tab (single source of truth: URL)
  useEffect(() => {
    const urlTab = searchParams.get('tab')
    const targetTab = urlTab || 'overview'

    // Only update if URL changed (don't include activeTab in deps to avoid loop)
    setActiveTab(targetTab)
  }, [searchParams])

  // Fetch settings from backend
  const { data: settings, isLoading, error, refetch } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await apiClient.request<{ success: boolean; data: BackendSettings }>(
        '/wp-json/redd-miner-leads/v1/settings'
      )
      return response.data
    },
  })

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: async (newSettings: Partial<BackendSettings>) => {
      return await apiClient.request<{ success: boolean; message: string }>(
        '/wp-json/redd-miner-leads/v1/settings',
        {
          method: 'PUT',
          body: JSON.stringify(newSettings),
        }
      )
    },
    onSuccess: () => {
      toast.success('Settings updated successfully!')
      queryClient.invalidateQueries({ queryKey: ['settings'] })
    },
    onError: (error: any) => {
      toast.error(`Failed to update settings: ${error.message}`)
    },
  })

  // Test connection mutation
  const testConnectionMutation = useMutation({
    mutationFn: async (service: 'reddit' | 'openai') => {
      return await apiClient.request<{ success: boolean; message: string; data?: any }>(
        '/wp-json/redd-miner-leads/v1/settings/test-connection',
        {
          method: 'POST',
          body: JSON.stringify({ service }),
        }
      )
    },
    onSuccess: (data) => {
      if (data.success) {
        toast.success(data.message)
      } else {
        toast.error(data.message)
      }
      setTestingConnection(null)
    },
    onError: (error: any) => {
      toast.error(`Connection test failed: ${error.message}`)
      setTestingConnection(null)
    },
  })

  // Form setups
  const redditForm = useForm({
    resolver: yupResolver(redditSchema),
    defaultValues: {
      reddit_client_id: settings?.reddit_client_id || '',
      reddit_client_secret: '',
      reddit_user_agent: settings?.reddit_user_agent || 'ReddMinerLeads/1.0',
    }
  })

  const aiForm = useForm({
    resolver: yupResolver(aiSchema),
    defaultValues: {
      openai_api_key: '',
      anthropic_api_key: '',
      ai_settings: {
        default_provider: settings?.ai_settings?.default_provider || 'openai',
        default_openai_model: settings?.ai_settings?.default_openai_model || 'gpt-3.5-turbo',
        default_anthropic_model: settings?.ai_settings?.default_anthropic_model || 'claude-3-sonnet-20240229',
        temperature: settings?.ai_settings?.temperature || 0.7,
        max_tokens: settings?.ai_settings?.max_tokens || 300,
      }
    }
  })

  const monitoringForm = useForm({
    resolver: yupResolver(monitoringSchema),
    defaultValues: {
      monitoring_frequency: settings?.monitoring_frequency || 5,
      daily_api_limit: settings?.daily_api_limit || 1000,
      enable_auto_reply: settings?.enable_auto_reply || false,
    }
  })

  const profileForm = useForm({
    resolver: yupResolver(profileSchema),
    defaultValues: {
      profile: {
        first_name: settings?.profile?.first_name || '',
        last_name: settings?.profile?.last_name || '',
        display_name: settings?.profile?.display_name || '',
        email: settings?.profile?.email || '',
      }
    }
  })

  const notificationsForm = useForm({
    resolver: yupResolver(notificationsSchema),
    defaultValues: {
      notifications: {
        email_opportunities: settings?.notifications?.email_opportunities || true,
        email_responses: settings?.notifications?.email_responses || true,
        browser_notifications: settings?.notifications?.browser_notifications || false,
        slack_webhook: settings?.notifications?.slack_webhook || '',
      }
    }
  })

  const passwordForm = useForm({
    resolver: yupResolver(passwordSchema),
    defaultValues: {
      current_password: '',
      new_password: '',
      confirm_password: '',
    }
  })

  // Redirect to first available tab if current tab is not accessible
  React.useEffect(() => {
    if (settings?.available_tabs && !settings.available_tabs.includes(activeTab)) {
      setActiveTab(settings.available_tabs[0] || 'overview')
    }
  }, [settings?.available_tabs, activeTab])

  // Update form defaults when settings load
  React.useEffect(() => {
    if (settings) {
      redditForm.reset({
        reddit_client_id: settings.reddit_client_id,
        reddit_client_secret: '',
        reddit_user_agent: settings.reddit_user_agent,
      })

      // Only reset AI form if ai_settings exists (admin-only field)
      if (settings.ai_settings) {
        aiForm.reset({
          openai_api_key: '',
          anthropic_api_key: '',
          ai_settings: settings.ai_settings
        })
      }

      // Only reset monitoring form if monitoring fields exist (admin-only fields)
      if (settings.monitoring_frequency !== undefined) {
        monitoringForm.reset({
          monitoring_frequency: settings.monitoring_frequency,
          daily_api_limit: settings.daily_api_limit,
          enable_auto_reply: settings.enable_auto_reply,
        })
      }

      profileForm.reset({
        profile: settings.profile
      })

      notificationsForm.reset({
        notifications: settings.notifications
      })
    }
  }, [settings, redditForm, aiForm, monitoringForm, profileForm, notificationsForm])

  // Form submission handlers
  const handleRedditSubmit = (data: any) => {
    updateSettingsMutation.mutate({
      reddit_client_id: data.reddit_client_id,
      reddit_client_secret: data.reddit_client_secret || undefined,
      reddit_user_agent: data.reddit_user_agent,
    })
  }

  const handleAiSubmit = (data: any) => {
    const updateData: Partial<BackendSettings> = {
      ai_settings: data.ai_settings
    }

    if (data.openai_api_key) {
      updateData.openai_api_key = data.openai_api_key
    }

    if (data.anthropic_api_key) {
      updateData.anthropic_api_key = data.anthropic_api_key
    }

    updateSettingsMutation.mutate(updateData)
  }

  const handleMonitoringSubmit = (data: any) => {
    updateSettingsMutation.mutate(data)
  }

  const handleProfileSubmit = (data: any) => {
    updateSettingsMutation.mutate(data)
  }

  const handleNotificationsSubmit = (data: any) => {
    updateSettingsMutation.mutate(data)
  }

  const handlePasswordSubmit = async (data: any) => {
    try {
      // First, get the current user info for re-authentication
      const currentUser = settings?.profile?.email || settings?.profile?.display_name

      const response = await apiClient.request<{ success: boolean; message: string; token?: string }>(
        '/wp-json/redd-miner-leads/v1/settings/change-password',
        {
          method: 'POST',
          body: JSON.stringify({
            current_password: data.current_password,
            new_password: data.new_password,
            confirm_password: data.confirm_password,
          }),
        }
      )

      if (response.success) {
        toast.success('Password changed successfully!')
        passwordForm.reset()

        // If backend returns a new token, use it
        if (response.token) {
          apiClient.setToken(response.token)
          toast.success('Your session has been refreshed automatically.')
        } else {
          // Otherwise, get username from settings and re-authenticate
          toast.loading('Refreshing your session...', { duration: 1000 })

          try {
            // Re-authenticate with new password to get fresh token
            const username = settings?.profile?.display_name || currentUser
            await apiClient.login({ username, password: data.new_password })
            toast.success('Session refreshed! You can continue working.')
          } catch (error) {
            // If auto-login fails, redirect to login
            toast.error('Please log in again with your new password.')
            setTimeout(() => {
              apiClient.logout()
              window.location.href = '/login?password_changed=true'
            }, 2000)
          }
        }
      } else {
        toast.error(response.message || 'Failed to change password')
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to change password')
    }
  }

  // User Reddit App handlers
  const handleSaveRedditApp = async (data: RedditAppFormData) => {
    try {
      await apiClient.request(
        '/wp-json/redd-miner-leads/v1/user/reddit-app',
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      )
      toast.success('Reddit app credentials saved successfully!')
      refetch()
    } catch (error: any) {
      toast.error(error.message || 'Failed to save Reddit app')
      throw error
    }
  }

  const handleDeleteRedditApp = async () => {
    try {
      console.log('🗑️ DELETE: Starting Reddit app deletion...')

      await apiClient.request(
        '/wp-json/redd-miner-leads/v1/user/reddit-app',
        {
          method: 'DELETE',
        }
      )

      console.log('🗑️ DELETE: Backend deletion successful')

      // Manually update cache to immediately reflect deletion
      queryClient.setQueryData(['settings'], (oldData: any) => {
        console.log('🗑️ DELETE: Current cache data:', oldData)
        if (!oldData) return oldData
        const newData = {
          ...oldData,
          user_reddit_app: {
            has_app: false,
            app: null
          }
        }
        console.log('🗑️ DELETE: New cache data:', newData)
        return newData
      })

      toast.success('Reddit app deleted successfully')

      // Force refetch to sync with backend
      console.log('🗑️ DELETE: Refetching from backend...')
      const result = await refetch()
      console.log('🗑️ DELETE: Refetch result:', result.data)
    } catch (error: any) {
      console.error('🗑️ DELETE: Error:', error)
      toast.error(error.message || 'Failed to delete Reddit app')
      throw error
    }
  }

  const handleConnectRedditAccount = async (): Promise<string> => {
    try {
      const response = await apiClient.request<{ success: boolean; data: { auth_url: string } }>(
        '/wp-json/redd-miner-leads/v1/user/reddit-app/auth-url'
      )
      if (response.success && response.data.auth_url) {
        return response.data.auth_url
      }
      throw new Error('Failed to get OAuth URL')
    } catch (error: any) {
      toast.error(error.message || 'Failed to start OAuth flow')
      throw error
    }
  }

  const handleDisconnectRedditAccount = async () => {
    try {
      await apiClient.request(
        '/wp-json/redd-miner-leads/v1/user/reddit-app/disconnect',
        {
          method: 'POST',
        }
      )
      refetch()
    } catch (error: any) {
      toast.error(error.message || 'Failed to disconnect Reddit account')
      throw error
    }
  }

  const handleTestConnection = (service: 'reddit' | 'openai') => {
    setTestingConnection(service)
    testConnectionMutation.mutate(service)
  }

  const allTabs = [
    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
    { id: 'profile', name: 'Profile', icon: UserIcon },
    { id: 'notifications', name: 'Notifications', icon: BellIcon },
    { id: 'reddit', name: 'Reddit API', icon: KeyIcon },
    { id: 'ai', name: 'AI Services', icon: WrenchScrewdriverIcon },
    { id: 'monitoring', name: 'Monitoring', icon: CogIcon },
  ]

  // Filter tabs based on user's available_tabs from backend
  const tabs = settings?.available_tabs
    ? allTabs.filter(tab => settings.available_tabs?.includes(tab.id))
    : allTabs

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !settings) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-4">
          <ExclamationTriangleIcon className="h-12 w-12 mx-auto mb-4" />
          Failed to load settings
        </div>
        <p className="text-gray-600 mb-6">
          Unable to connect to the settings API. Please check your connection and try again.
        </p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="border-b border-gray-200 pb-5">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="mt-2 text-sm text-gray-600">
          Configure your Reddit Miner Leads application settings and API integrations.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSearchParams({ tab: tab.id }, { replace: true })}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-5 w-5 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Connection Status */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                <ShieldCheckIcon className="h-5 w-5 mr-2 text-green-500" />
                Connection Status
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Reddit API</span>
                  <div className="flex items-center">
                    {settings.reddit_client_id && settings.reddit_client_secret_masked ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        Connected
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                        Not Connected
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">OpenAI API</span>
                  <div className="flex items-center">
                    {settings.openai_api_key_masked ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        Connected
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                        Not Connected
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Anthropic API</span>
                  <div className="flex items-center">
                    {settings.anthropic_api_key_masked ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        Connected
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        <InformationCircleIcon className="h-4 w-4 mr-1" />
                        Optional
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Reddit Apps Status */}
            {settings.reddit_apps && settings.reddit_apps.length > 0 && (
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                  <ChartBarIcon className="h-5 w-5 mr-2 text-blue-500" />
                  Reddit Apps Usage
                </h3>
                <div className="space-y-4">
                  {settings.reddit_apps.map((app, index) => (
                    <div key={index} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">{app.app_name}</span>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          app.can_make_requests
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {app.can_make_requests ? 'Active' : 'Limited'}
                        </span>
                      </div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Usage: {app.current_usage}/{app.daily_limit}</span>
                          <span className="text-gray-600">Health: {app.health_score}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              app.current_usage >= app.daily_limit ? 'bg-red-500' : 'bg-blue-500'
                            }`}
                            style={{ width: `${Math.min((app.current_usage / app.daily_limit) * 100, 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Monitoring Status - Only show if user has access to monitoring settings */}
            {settings.monitoring_frequency !== undefined && (
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                  <CogIcon className="h-5 w-5 mr-2 text-purple-500" />
                  Monitoring Configuration
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Check Frequency</span>
                    <span className="text-sm text-gray-900">{settings.monitoring_frequency} minutes</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Daily API Limit</span>
                    <span className="text-sm text-gray-900">{settings.daily_api_limit} requests</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Auto Reply</span>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      settings.enable_auto_reply
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {settings.enable_auto_reply ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* AI Configuration - Only show if user has access to AI settings */}
            {settings.ai_settings && (
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                  <WrenchScrewdriverIcon className="h-5 w-5 mr-2 text-orange-500" />
                  AI Configuration
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Default Provider</span>
                    <span className="text-sm text-gray-900 capitalize">{settings.ai_settings.default_provider}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Default Model</span>
                    <span className="text-sm text-gray-900">
                      {settings.ai_settings.default_provider === 'openai'
                        ? settings.ai_settings.default_openai_model
                        : settings.ai_settings.default_anthropic_model}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Temperature</span>
                    <span className="text-sm text-gray-900">{settings.ai_settings.temperature}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Max Tokens</span>
                    <span className="text-sm text-gray-900">{settings.ai_settings.max_tokens}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
              <UserIcon className="h-5 w-5 mr-2 text-blue-500" />
              Profile Settings
            </h3>
            <form onSubmit={profileForm.handleSubmit(handleProfileSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">First Name</label>
                  <input
                    {...profileForm.register('profile.first_name')}
                    type="text"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Your first name"
                  />
                  {profileForm.formState.errors.profile?.first_name && (
                    <p className="mt-1 text-sm text-red-600">{profileForm.formState.errors.profile.first_name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Last Name</label>
                  <input
                    {...profileForm.register('profile.last_name')}
                    type="text"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Your last name"
                  />
                  {profileForm.formState.errors.profile?.last_name && (
                    <p className="mt-1 text-sm text-red-600">{profileForm.formState.errors.profile.last_name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Display Name</label>
                  <input
                    {...profileForm.register('profile.display_name')}
                    type="text"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="How your name appears"
                  />
                  {profileForm.formState.errors.profile?.display_name && (
                    <p className="mt-1 text-sm text-red-600">{profileForm.formState.errors.profile.display_name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    {...profileForm.register('profile.email')}
                    type="email"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="your.email@example.com"
                  />
                  {profileForm.formState.errors.profile?.email && (
                    <p className="mt-1 text-sm text-red-600">{profileForm.formState.errors.profile.email.message}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <img
                      className="h-12 w-12 rounded-full"
                      src={settings?.profile?.avatar_url}
                      alt="Profile"
                    />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-900">Profile Avatar</p>
                    <p className="text-sm text-gray-500">Managed through WordPress Gravatar</p>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={updateSettingsMutation.isPending}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {updateSettingsMutation.isPending ? 'Saving...' : 'Update Profile'}
                </button>
              </div>
            </form>

            {/* Password Change Section */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                <KeyIcon className="h-5 w-5 mr-2 text-red-500" />
                Change Password
              </h3>
              <form onSubmit={passwordForm.handleSubmit(handlePasswordSubmit)} className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex">
                    <ShieldCheckIcon className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-700">
                      <p className="font-medium mb-1">Password Requirements:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Minimum 8 characters</li>
                        <li>At least one uppercase letter</li>
                        <li>At least one lowercase letter</li>
                        <li>At least one number</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Current Password <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-1 relative">
                      <input
                        {...passwordForm.register('current_password')}
                        type={showPasswords ? 'text' : 'password'}
                        autoComplete="current-password"
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 pr-10"
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(!showPasswords)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showPasswords ? (
                          <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {passwordForm.formState.errors.current_password && (
                      <p className="mt-1 text-sm text-red-600">
                        {passwordForm.formState.errors.current_password.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      New Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      {...passwordForm.register('new_password')}
                      type={showPasswords ? 'text' : 'password'}
                      autoComplete="new-password"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter new password"
                    />
                    {passwordForm.formState.errors.new_password && (
                      <p className="mt-1 text-sm text-red-600">
                        {passwordForm.formState.errors.new_password.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Confirm New Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      {...passwordForm.register('confirm_password')}
                      type={showPasswords ? 'text' : 'password'}
                      autoComplete="new-password"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Re-enter new password"
                    />
                    {passwordForm.formState.errors.confirm_password && (
                      <p className="mt-1 text-sm text-red-600">
                        {passwordForm.formState.errors.confirm_password.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <p className="text-sm text-gray-500">
                    For security, you'll need to enter your current password to make changes.
                  </p>
                  <button
                    type="submit"
                    disabled={updateSettingsMutation.isPending}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                  >
                    {updateSettingsMutation.isPending ? 'Updating...' : 'Change Password'}
                  </button>
                </div>
              </form>
            </div>

            {/* Connected Accounts Section */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <ConnectedAccounts />
            </div>
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
              <BellIcon className="h-5 w-5 mr-2 text-yellow-500" />
              Notification Settings
            </h3>
            <form onSubmit={notificationsForm.handleSubmit(handleNotificationsSubmit)} className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Email Opportunities</label>
                    <p className="text-sm text-gray-500">Get notified when new opportunities are found</p>
                  </div>
                  <input
                    {...notificationsForm.register('notifications.email_opportunities')}
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Email Responses</label>
                    <p className="text-sm text-gray-500">Get notified when AI generates responses</p>
                  </div>
                  <input
                    {...notificationsForm.register('notifications.email_responses')}
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Browser Notifications</label>
                    <p className="text-sm text-gray-500">Show browser notifications for important updates</p>
                  </div>
                  <input
                    {...notificationsForm.register('notifications.browser_notifications')}
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Slack Webhook URL</label>
                  <input
                    {...notificationsForm.register('notifications.slack_webhook')}
                    type="url"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://hooks.slack.com/services/..."
                  />
                  {notificationsForm.formState.errors.notifications?.slack_webhook && (
                    <p className="mt-1 text-sm text-red-600">{notificationsForm.formState.errors.notifications.slack_webhook.message}</p>
                  )}
                  <p className="mt-2 text-sm text-gray-500">Optional: Get notifications in your Slack channel</p>
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <button
                  type="submit"
                  disabled={updateSettingsMutation.isPending}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {updateSettingsMutation.isPending ? 'Saving...' : 'Update Notifications'}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'reddit' && (
          <div className="space-y-6">
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                <KeyIcon className="h-5 w-5 mr-2 text-red-500" />
                Reddit API Configuration
              </h3>

              {/* Setup Guide (show if no app) */}
              {!settings.user_reddit_app?.has_app && (
                <RedditAppSetupGuide
                  redirectUri={(import.meta.env.VITE_API_URL || 'https://admin.reddminer.com') + '/wp-json/redd-miner-leads/v1/user/reddit-app/oauth-callback'}
                />
              )}

              {/* Credentials Form */}
              <RedditAppCredentialsForm
                existingApp={settings.user_reddit_app?.app ? {
                  app_name: settings.user_reddit_app.app.app_name,
                  client_id_masked: settings.user_reddit_app.app.client_id_masked,
                  client_secret_masked: settings.user_reddit_app.app.client_secret_masked,
                  user_agent: settings.user_reddit_app.app.user_agent,
                } : undefined}
                onSave={handleSaveRedditApp}
                onDelete={settings.user_reddit_app?.has_app ? handleDeleteRedditApp : undefined}
                isUpdating={updateSettingsMutation.isPending}
              />
            </div>

            {/* OAuth Connection (show if has app) */}
            {settings.user_reddit_app?.has_app && settings.user_reddit_app.app && (
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-6">
                  Reddit Account Connection
                </h3>
                <RedditOAuthButton
                  isAuthorized={settings.user_reddit_app.app.oauth_authorized}
                  onConnect={handleConnectRedditAccount}
                  onDisconnect={handleDisconnectRedditAccount}
                />
              </div>
            )}

            {/* Usage Stats (show if has app with OAuth) */}
            {settings.user_reddit_app?.has_app &&
             settings.user_reddit_app.app &&
             settings.user_reddit_app.app.oauth_authorized && (
              <RedditAppUsageStats
                currentUsage={settings.user_reddit_app.app.current_usage}
                dailyLimit={settings.user_reddit_app.app.daily_limit}
                healthScore={settings.user_reddit_app.app.health_score}
                lastReset={settings.user_reddit_app.app.last_reset}
                status={settings.user_reddit_app.app.status}
                canMakeRequests={settings.user_reddit_app.can_make_requests}
              />
            )}
          </div>
        )}

        {activeTab === 'ai' && (
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
              <WrenchScrewdriverIcon className="h-5 w-5 mr-2 text-orange-500" />
              AI Services Configuration
            </h3>

            <form onSubmit={aiForm.handleSubmit(handleAiSubmit)} className="space-y-6">
              {/* Provider Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Default AI Provider</label>
                <div className="grid grid-cols-2 gap-4">
                  <label className="relative flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      {...aiForm.register('ai_settings.default_provider')}
                      type="radio"
                      value="openai"
                      className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <div className="ml-3">
                      <span className="block text-sm font-medium text-gray-900">OpenAI</span>
                      <span className="block text-xs text-gray-500">GPT models</span>
                    </div>
                  </label>
                  <label className="relative flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      {...aiForm.register('ai_settings.default_provider')}
                      type="radio"
                      value="anthropic"
                      className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <div className="ml-3">
                      <span className="block text-sm font-medium text-gray-900">Anthropic</span>
                      <span className="block text-xs text-gray-500">Claude models</span>
                    </div>
                  </label>
                </div>
              </div>

              {/* OpenAI Configuration */}
              <div className="border rounded-lg p-4">
                <h4 className="text-md font-medium text-gray-900 mb-4">OpenAI Configuration</h4>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">API Key</label>
                    <div className="mt-1 relative">
                      <input
                        {...aiForm.register('openai_api_key')}
                        type={showApiKeys ? 'text' : 'password'}
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 pr-10"
                        placeholder={settings.openai_api_key_masked || "Enter new OpenAI API key"}
                      />
                      <button
                        type="button"
                        onClick={() => setShowApiKeys(!showApiKeys)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showApiKeys ? (
                          <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {settings.openai_api_key_masked && (
                      <p className="mt-1 text-sm text-gray-500">
                        Current: {settings.openai_api_key_masked}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Default Model</label>
                    <select
                      {...aiForm.register('ai_settings.default_openai_model')}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                      <option value="gpt-4">GPT-4</option>
                      <option value="gpt-4-turbo-preview">GPT-4 Turbo Preview</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Anthropic Configuration */}
              <div className="border rounded-lg p-4">
                <h4 className="text-md font-medium text-gray-900 mb-4">Anthropic Configuration</h4>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">API Key</label>
                    <div className="mt-1 relative">
                      <input
                        {...aiForm.register('anthropic_api_key')}
                        type={showApiKeys ? 'text' : 'password'}
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 pr-10"
                        placeholder={settings.anthropic_api_key_masked || "Enter new Anthropic API key"}
                      />
                      <button
                        type="button"
                        onClick={() => setShowApiKeys(!showApiKeys)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showApiKeys ? (
                          <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {settings.anthropic_api_key_masked && (
                      <p className="mt-1 text-sm text-gray-500">
                        Current: {settings.anthropic_api_key_masked}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Default Model</label>
                    <select
                      {...aiForm.register('ai_settings.default_anthropic_model')}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="claude-3-sonnet-20240229">Claude 3 Sonnet</option>
                      <option value="claude-3-opus-20240229">Claude 3 Opus</option>
                      <option value="claude-3-haiku-20240307">Claude 3 Haiku</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* AI Parameters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Temperature</label>
                  <input
                    {...aiForm.register('ai_settings.temperature')}
                    type="number"
                    step="0.1"
                    min="0"
                    max="2"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">Controls randomness (0.0 = deterministic, 2.0 = very random)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Max Tokens</label>
                  <input
                    {...aiForm.register('ai_settings.max_tokens')}
                    type="number"
                    min="50"
                    max="4000"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">Maximum response length</p>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t">
                <button
                  type="button"
                  onClick={() => handleTestConnection('openai')}
                  disabled={testingConnection === 'openai'}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {testingConnection === 'openai' ? (
                    <LoadingSpinner size="sm" className="mr-2" />
                  ) : (
                    <PlayIcon className="h-4 w-4 mr-2" />
                  )}
                  Test AI Connection
                </button>

                <button
                  type="submit"
                  disabled={updateSettingsMutation.isPending}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {updateSettingsMutation.isPending ? (
                    <LoadingSpinner size="sm" className="mr-2" />
                  ) : null}
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'monitoring' && (
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
              <CogIcon className="h-5 w-5 mr-2 text-purple-500" />
              Monitoring Configuration
            </h3>

            <form onSubmit={monitoringForm.handleSubmit(handleMonitoringSubmit)} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Monitoring Frequency</label>
                <div className="mt-1 relative">
                  <input
                    {...monitoringForm.register('monitoring_frequency')}
                    type="number"
                    min="1"
                    max="60"
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">minutes</span>
                  </div>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  How often to check for new opportunities (1-60 minutes)
                </p>
                {monitoringForm.formState.errors.monitoring_frequency && (
                  <p className="mt-1 text-sm text-red-600">{monitoringForm.formState.errors.monitoring_frequency.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Daily API Limit</label>
                <div className="mt-1 relative">
                  <input
                    {...monitoringForm.register('daily_api_limit')}
                    type="number"
                    min="10"
                    max="10000"
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">requests</span>
                  </div>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Maximum API requests per day to avoid rate limits
                </p>
                {monitoringForm.formState.errors.daily_api_limit && (
                  <p className="mt-1 text-sm text-red-600">{monitoringForm.formState.errors.daily_api_limit.message}</p>
                )}
              </div>

              <div className="flex items-center">
                <input
                  {...monitoringForm.register('enable_auto_reply')}
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  Enable automatic AI responses
                </label>
              </div>
              <p className="text-sm text-gray-500 ml-6">
                Automatically generate and post AI responses to detected opportunities
              </p>

              <div className="pt-4 border-t">
                <button
                  type="submit"
                  disabled={updateSettingsMutation.isPending}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {updateSettingsMutation.isPending ? (
                    <LoadingSpinner size="sm" className="mr-2" />
                  ) : null}
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        )}
      </motion.div>
    </div>
  )
}

export default SettingsPage